import { dismissAlert, showError } from '@calendly/alert';
import { useFlag } from '@calendly/feature-check-react';
import { useFormContext, useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { TagContainer, TagInput } from '@calendly/tags';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { Loader } from '@calendly/ui/components/loader';
import { Menu, Item } from '@calendly/ui/components/menu';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { RemoteContactsSearchHandler } from 'features/crm';
import { useBooleanState } from 'hooks/useBooleanState';
import { useDebounce } from 'hooks/useDebounce';
import { isBackspaceKey, isCommaKey, isEnterKey, isArrowDownKey, isArrowUpKey, isTabKey, isEscapeKey } from 'services/keycodes';
import { hasToManyEmails } from '../get_validation_schema';
import { ContactItem } from './contact_item';
import { ContactTag } from './contact_tag';
import { ContactsSearchHandler } from './contacts_search_handler';
const containerIsPhoneCss = "c1fwsqdc";
const containerCss = "c1gz4ke3";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledTagContainerCss = "s2jm8nv";
const StyledTagContainer = forwardRef((props, ref) => <TagContainer ref={ref} {...props} className={classNames(styledTagContainerCss, props.className)} />);
const styledItemIsHighlightedCss = "shsr33k";
const styledItemCss = "sxi54be";
const StyledItem = forwardRef((props, ref) => {
  const {
    highlightedIndex,
    index,
    ...restProps
  } = props;
  const role = 'option';
  const id = `contacts-search-item-${index}`;
  const isHighlighted = highlightedIndex === index;
  const tabIndex = -1;
  return <Item ref={ref} {...restProps} role={role} id={id} tabIndex={tabIndex} aria-selected={highlightedIndex === index} className={classNames(styledItemCss, {
    [styledItemIsHighlightedCss]: isHighlighted
  }, props.className)} />;
});
const dropdownMenuIsPhoneCss = "dxrwjje";
const dropdownMenuCss = "d1dz8q6l";
const DropdownMenu = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Menu ref={ref} {...props} className={classNames(dropdownMenuCss, {
    [dropdownMenuIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledTagInputContainerCss = "sulw2au";
const StyledTagInputContainer = forwardRef((props, ref) => {
  const {
    isExpanded,
    highlightedIndex,
    ...restProps
  } = props;
  const id = 'contacts-search-input';
  return <TagInput ref={ref} {...restProps} id={id} aria-autocomplete="list" aria-controls="contacts-search-list" aria-activedescendant={highlightedIndex >= 0 ? `contacts-search-item-${highlightedIndex}` : undefined} aria-expanded={isExpanded ? 'true' : 'false'} className={classNames(styledTagInputContainerCss, props.className)} />;
});
const emptyContainerCss = "e10nu4on";
const EmptyContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyContainerCss, props.className)} />;
});
const DELIMITER_CHARS = [',', ';', '\n', '\t'];
export const ContactsContainer = ({
  id,
  isSingleSelection,
  placeholderText,
  push,
  fieldName,
  remove,
  isDisabled
}) => {
  const [{
    value: items
  }, meta, {
    setTouched
  }] = useFormField({
    name: fieldName
  });
  const dispatch = useDispatch();
  const [{
    value: inputValue
  }, _, {
    setValue: setInputValue
  }] = useFormField({
    name: `${fieldName}CurrentInputValue`
  });
  const {
    values: {
      to,
      cc,
      bcc
    }
  } = useFormContext();
  const tagInputRef = useRef(null);
  const [placeholder, setPlaceholder] = useState(placeholderText);
  const [toggleDropdown, Dropdown, isOpen] = useDropdown();
  const [isFocused, focus, blur] = useBooleanState(true);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const debouncedQuery = useDebounce(inputValue, 300);
  const debouncedInputValue = useDebounce(inputValue, 500);
  const debouncedIsFocused = useDebounce(isFocused, 30);
  const displayItems = debouncedIsFocused ? items : items.slice(0, 1);
  const renderDropdown = Boolean(inputValue || debouncedQuery);
  const isCRMContactsPageEnabled = useFlag('crm_contacts_page_to_ai_crm_feature');
  const ContactsSearchComponent = isCRMContactsPageEnabled ? RemoteContactsSearchHandler : ContactsSearchHandler;
  const [contacts, setContacts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const isLoading = isFetching || !!inputValue && inputValue !== debouncedInputValue;
  const handleResults = useCallback(results => {
    setContacts(results);
    setIsFetching(false);
    setIsSuccess(true);
  }, []);
  useEffect(() => {
    if (!renderDropdown) {
      setHighlightedIndex(0);
    }
  }, [renderDropdown]);
  const isNameMatch = contact => {
    if (!debouncedQuery) return false;
    return contact.name?.toLowerCase().includes(debouncedQuery.trim().toLowerCase());
  };
  const isEmailMatch = contact => {
    if (!debouncedQuery) return false;
    return contact.email?.toLowerCase().includes(debouncedQuery.trim().toLowerCase());
  };
  const availableContacts = contacts.filter(contact => !to.some(item => item.email === contact.email) && !cc.some(item => item.email === contact.email) && !bcc.some(item => item.email === contact.email)).filter(contact => isNameMatch(contact) || isEmailMatch(contact));
  const isEmailsLimitExceeded = useCallback(() => hasToManyEmails({
    cc,
    bcc,
    [`${fieldName}CurrentInputValue`]: inputValue
  }), [fieldName, inputValue, cc, bcc]);
  const isEmailInCcOrBcc = useCallback(() => {
    if (!inputValue?.length) return false;
    const lowerCaseValue = inputValue.toLowerCase();
    const emailExistsInCc = cc.some(item => item.email.toLowerCase() === lowerCaseValue);
    const emailExistsInBcc = bcc.some(item => item.email.toLowerCase() === lowerCaseValue);
    return emailExistsInCc || emailExistsInBcc;
  }, [inputValue, cc, bcc]);
  useEffect(() => {
    if (fieldName !== 'to' && items.length > 1 && !isFocused) {
      const length = items.length - 1;
      const other = length === 1 ? 'other' : 'others';
      setPlaceholder(`+ ${length} ${other}`);
    } else if (items.length > 0) {
      setPlaceholder('');
    } else {
      setPlaceholder(placeholderText);
    }
  }, [items, placeholderText, fieldName, cc, bcc, isFocused]);
  const handleAdd = useCallback((forcedValue = null) => {
    const value = (forcedValue || inputValue)?.trim();
    if (value === undefined) return;
    if (isEmailsLimitExceeded()) return;
    if (isEmailInCcOrBcc()) {
      dispatch(showError(I18n.t('publisher.send_from_calendly.errors.duplicate_email')));
      return;
    }
    if (value.length) {
      setTouched(true);
      setPlaceholder('');
      dispatch(dismissAlert());
      const selectedContact = availableContacts.find(contact => contact.email.toLowerCase() === value.toLowerCase());
      if (selectedContact) {
        push(selectedContact);
      } else {
        push({
          email: value
        });
      }
    }
    setInputValue('');
  }, [push, inputValue, setInputValue, setTouched, availableContacts, isEmailInCcOrBcc, isEmailsLimitExceeded, dispatch]);
  const handleBeforeInput = useCallback(event => {
    setHighlightedIndex(0);
    if (DELIMITER_CHARS.includes(event.data)) {
      handleAdd();
      event.preventDefault();
    }
  }, [handleAdd]);
  const handleKeyDown = useCallback(event => {
    if ((isTabKey(event) || isEscapeKey(event)) && !isOpen) return;
    if (!isOpen) toggleDropdown();
    if (isArrowDownKey(event)) {
      setHighlightedIndex(prevIndex => prevIndex < availableContacts.length - 1 ? prevIndex + 1 : 0);
      event.preventDefault();
    } else if (isArrowUpKey(event)) {
      setHighlightedIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : availableContacts.length - 1);
      event.preventDefault();
    } else if ((isEnterKey(event) || isTabKey(event)) && isOpen) {
      handleAdd(availableContacts[highlightedIndex]?.email);
      toggleDropdown();
      event.preventDefault();
    } else if (isBackspaceKey(event)) {
      if (!inputValue.length && items.length) {
        remove(items.length - 1);
      }
    } else if (isCommaKey(event)) {
      handleAdd();
      toggleDropdown();
      event.preventDefault();
    }
  }, [handleAdd, inputValue, isOpen, items, availableContacts, remove, toggleDropdown, highlightedIndex]);
  const handleMouseDown = useCallback(() => {
    if (!isOpen) toggleDropdown();
  }, [isOpen, toggleDropdown]);
  const onRemoveHandler = useCallback(index => {
    remove(index);
  }, [remove]);
  const onLineItemTabClick = useCallback(contact => event => {
    if (isTabKey(event)) {
      handleAdd(contact.email);
      event.preventDefault();
    }
  }, [handleAdd]);
  const handleBlur = useCallback(event => {
    if (inputValue && inputValue.trim().length > 0 && !event.relatedTarget?.closest('#contacts-search-list')) {
      handleAdd();
    }
  }, [handleAdd, inputValue]);
  return <Container onFocus={focus} onBlur={blur}>
      <ContactsSearchComponent query={debouncedQuery} onResults={handleResults} onFetching={setIsFetching} />
      <StyledTagContainer ref={tagInputRef} onKeyDown={isDisabled ? undefined : handleKeyDown} onInputChange={isDisabled ? undefined : setInputValue} onBeforeInput={isDisabled ? undefined : handleBeforeInput} onMouseDown={isDisabled ? undefined : handleMouseDown} onBlur={isDisabled ? undefined : handleBlur} inputValue={inputValue} inputAttributes={{
      id,
      name: fieldName,
      'aria-expanded': true,
      'aria-label': placeholder,
      placeholder: `${placeholder}`
    }}>
        {displayItems.map((contact, index) => <ContactTag key={contact.email || contact.name || index} item={contact} onDelete={() => {
        onRemoveHandler(index);
        blur();
      }} isSingle={isSingleSelection} isDisabled={isDisabled} isInvalid={Array.isArray(meta.error) && Boolean(meta.error[index])} />)}
        {!isDisabled && (isSingleSelection && items.length === 0 || !isSingleSelection) && <StyledTagInputContainer isExpanded={renderDropdown} highlightedIndex={highlightedIndex} />}
      </StyledTagContainer>
      {renderDropdown && <Dropdown>
          <DropdownMenu id="contacts-search-list" role="listbox">
            {isLoading && <EmptyContainer>
                <Loader size="small" inheritColor />
              </EmptyContainer>}
            {!isLoading && (isSuccess && availableContacts.length > 0 ? availableContacts.map((contact, index) => <StyledItem key={contact.email} onClick={() => handleAdd(contact.email)} onMouseEnter={() => setHighlightedIndex(index)} highlightedIndex={highlightedIndex} onKeyDown={onLineItemTabClick(contact)} index={index}>
                    <ContactItem contact={contact} />
                  </StyledItem>) : <EmptyContainer>
                  <BodyText color="muted">{I18n.t('publisher.send_from_calendly.errors.empty_contacts_search')}</BodyText>
                </EmptyContainer>)}
          </DropdownMenu>
        </Dropdown>}
    </Container>;
};

require("./contacts_container.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./contacts_container.js");